import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  FaDiscord,
  FaBars,
  FaTimes,
  FaCopy,
  FaCheck,
  FaBell,
  FaChevronDown,
  FaCrown,
  FaCoins,
  FaUser,
  FaUsers,
  FaSignOutAlt,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { SERVER_HOST } from '../../config/server';
import adminService from '../../services/adminService';
import discordService from '../../services/discordService';
import { fetchPlayerAccount } from '../../services/accountService';
import { fetchSiteConfig, DEFAULT_SITE_CONFIG } from '../../services/siteConfigService';
import './Navbar.css';

const DISCORD_URL = 'https://discord.gg/9W4Vusc4m2';
const NOTIF_READ_KEY = 'nexus_nav_notif_read';

const discordAvatarUrl = (user, size = 64) => {
  if (!user?.id) return null;
  if (user.avatar) {
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=${size}`;
  }
  const index = Number(String(user.id).slice(-2)) % 6;
  return `https://cdn.discordapp.com/embed/avatars/${index}.png`;
};

const readNotifIds = () => {
  try {
    const raw = localStorage.getItem(NOTIF_READ_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const writeNotifIds = (ids) => {
  try {
    localStorage.setItem(NOTIF_READ_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return '';
  }
};

const Navbar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [siteConfig, setSiteConfig] = useState(DEFAULT_SITE_CONFIG);
  const [player, setPlayer] = useState(() => discordService.getPlayerAuth());
  const [subscription, setSubscription] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [readIds, setReadIds] = useState(() => readNotifIds());
  const userMenuRef = useRef(null);

  const refreshAuth = useCallback(() => {
    setShowAdmin(Boolean(adminService.getAdminUser()));
    setPlayer(discordService.getPlayerAuth());
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [location.pathname, refreshAuth]);

  useEffect(() => {
    const onAuth = () => refreshAuth();
    window.addEventListener('nexus:auth-changed', onAuth);
    window.addEventListener('storage', onAuth);
    return () => {
      window.removeEventListener('nexus:auth-changed', onAuth);
      window.removeEventListener('storage', onAuth);
    };
  }, [refreshAuth]);

  useEffect(() => {
    let alive = true;
    if (!player?.id) {
      setSubscription(null);
      return undefined;
    }
    fetchPlayerAccount(player.id)
      .then((data) => {
        if (alive) setSubscription(data.subscription || null);
      })
      .catch(() => {
        if (alive) setSubscription(null);
      });
    return () => {
      alive = false;
    };
  }, [player?.id]);

  useEffect(() => {
    let alive = true;
    fetchSiteConfig().then((cfg) => {
      if (alive) setSiteConfig(cfg);
    });

    const onUpdate = (event) => {
      if (event.detail) setSiteConfig(event.detail);
    };
    window.addEventListener('nexus:site-config', onUpdate);

    return () => {
      alive = false;
      window.removeEventListener('nexus:site-config', onUpdate);
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setNotifOpen(false);
    setProfileOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (location.pathname !== '/' || location.hash !== '#como-entrar') return;
    const section = document.getElementById('como-entrar');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!notifOpen && !profileOpen) return undefined;
    const onPointer = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setNotifOpen(false);
        setProfileOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setNotifOpen(false);
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [notifOpen, profileOpen]);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const scrollToComoEntrar = useCallback(
    (event) => {
      if (location.pathname !== '/') return;
      event.preventDefault();
      closeMobileMenu();
      const section = document.getElementById('como-entrar');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        window.history.replaceState(null, '', '/#como-entrar');
      }
    },
    [location.pathname, closeMobileMenu]
  );

  const copyIp = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(SERVER_HOST);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, []);

  const notifications = useMemo(() => {
    if (!player?.id) return [];
    const list = [];
    const name = player.global_name || player.username || 'jogador';
    const vipActive =
      subscription &&
      ['active', 'past_due', 'trialing'].includes(subscription.status);

    list.push({
      id: `welcome-${player.id}`,
      tone: 'info',
      title: 'Conta conectada',
      body: `Olá, ${name}. Acesse Minha conta para VIP, pagamentos e status.`,
      href: '/conta',
      time: 'Agora',
    });

    if (vipActive && subscription?.currentPeriodEnd) {
      const days = daysUntil(subscription.currentPeriodEnd);
      const plan = subscription.planName || subscription.planId || 'VIP';
      if (days != null && days <= 7) {
        list.push({
          id: `vip-expiring-${subscription.currentPeriodEnd}`,
          tone: 'warn',
          title: 'VIP perto do vencimento',
          body: `${plan} expira em ${days <= 0 ? 'hoje' : `${days} dia${days === 1 ? '' : 's'}`} (${formatShortDate(subscription.currentPeriodEnd)}).`,
          href: '/conta',
          time: 'VIP',
        });
      } else {
        list.push({
          id: `vip-active-${subscription.planId || 'vip'}`,
          tone: 'ok',
          title: 'VIP ativo',
          body: `${plan} válido até ${formatShortDate(subscription.currentPeriodEnd)}.`,
          href: '/conta',
          time: 'VIP',
        });
      }
    } else if (subscription && !vipActive) {
      list.push({
        id: 'vip-inactive',
        tone: 'warn',
        title: 'Sem VIP ativo',
        body: 'Renove ou assine um plano para manter os benefícios no servidor.',
        href: '/vip',
        time: 'VIP',
      });
    }

    list.push({
      id: 'discord-community',
      tone: 'info',
      title: 'Comunidade Discord',
      body: 'Fique por dentro de eventos, updates e suporte da staff.',
      href: DISCORD_URL,
      external: true,
      time: 'Nexus',
    });

    return list;
  }, [player, subscription]);

  const vipActive = useMemo(
    () =>
      Boolean(
        subscription &&
          ['active', 'past_due', 'trialing'].includes(subscription.status)
      ),
    [subscription]
  );

  const unreadCount = useMemo(
    () => notifications.filter((n) => !readIds.has(n.id)).length,
    [notifications, readIds]
  );

  const markAllRead = useCallback(() => {
    const next = new Set(readIds);
    notifications.forEach((n) => next.add(n.id));
    setReadIds(next);
    writeNotifIds(next);
  }, [notifications, readIds]);

  const markOneRead = useCallback(
    (id) => {
      if (readIds.has(id)) return;
      const next = new Set(readIds);
      next.add(id);
      setReadIds(next);
      writeNotifIds(next);
    },
    [readIds]
  );

  const logout = useCallback(() => {
    discordService.clearPlayerAuth();
    setPlayer(null);
    setSubscription(null);
    setProfileOpen(false);
    setNotifOpen(false);
    closeMobileMenu();
    window.dispatchEvent(new Event('nexus:auth-changed'));
  }, [closeMobileMenu]);

  const navClass = ({ isActive }) =>
    `navbar-link${isActive ? ' navbar-link--active' : ''}`;

  const pages = siteConfig.pages || DEFAULT_SITE_CONFIG.pages;
  const sections = siteConfig.sections || DEFAULT_SITE_CONFIG.sections;
  const displayName = player?.global_name || player?.username || 'Conta';
  const avatarUrl = discordAvatarUrl(player, 64);

  return (
    <nav
      className={`navbar${isScrolled || isMobileMenuOpen ? ' navbar-scrolled' : ''}${isMobileMenuOpen ? ' navbar-open' : ''}`}
    >
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
          NEXUS
        </Link>

        <div
          className={`navbar-menu${isMobileMenuOpen ? ' navbar-menu-open' : ''}`}
          id="navbar-menu"
        >
          <div className="navbar-primary">
            <NavLink to="/" end className={navClass} onClick={closeMobileMenu}>
              Início
            </NavLink>
            {sections.homeJoin && (
              <Link
                to="/#como-entrar"
                className={`navbar-link${location.hash === '#como-entrar' && location.pathname === '/' ? ' navbar-link--active' : ''}`}
                onClick={scrollToComoEntrar}
              >
                Como Entrar
              </Link>
            )}
            {pages.download && (
              <NavLink to="/download" className={navClass} onClick={closeMobileMenu}>
                Download
              </NavLink>
            )}
            {pages.vip && (
              <NavLink to="/vip" className={navClass} onClick={closeMobileMenu}>
                Planos VIP
              </NavLink>
            )}
            {pages.economia && (
              <NavLink to="/economia" className={navClass} onClick={closeMobileMenu}>
                Economia
              </NavLink>
            )}
            {pages.team && (
              <NavLink to="/equipe" className={navClass} onClick={closeMobileMenu}>
                Equipe
              </NavLink>
            )}
            {showAdmin && (
              <NavLink to="/admin" className={navClass} onClick={closeMobileMenu}>
                Admin
              </NavLink>
            )}
          </div>

          <div className="navbar-actions">
            {pages.conta && (
              player ? (
                <div className="navbar-user" ref={userMenuRef}>
                  <button
                    type="button"
                    className={`navbar-notif${notifOpen ? ' navbar-notif--open' : ''}`}
                    aria-label={
                      unreadCount
                        ? `Notificações, ${unreadCount} não lidas`
                        : 'Notificações'
                    }
                    aria-expanded={notifOpen}
                    aria-haspopup="true"
                    onClick={() => {
                      setNotifOpen((open) => !open);
                      setProfileOpen(false);
                    }}
                  >
                    <FaBell aria-hidden="true" />
                    {unreadCount > 0 && (
                      <span className="navbar-notif__badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {notifOpen && (
                    <div className="navbar-dropdown navbar-dropdown--notif" role="menu">
                      <div className="navbar-dropdown__head">
                        <div>
                          <p className="navbar-dropdown__title">Notificações</p>
                          <p className="navbar-dropdown__sub">
                            {unreadCount
                              ? `${unreadCount} sem ler`
                              : 'Tudo em dia'}
                          </p>
                        </div>
                        {unreadCount > 0 && (
                          <button
                            type="button"
                            className="navbar-dropdown__action"
                            onClick={markAllRead}
                          >
                            Marcar lidas
                          </button>
                        )}
                      </div>

                      <ul className="navbar-dropdown__list">
                        {notifications.map((item) => {
                          const unread = !readIds.has(item.id);
                          const className = `navbar-dropdown__item${unread ? ' navbar-dropdown__item--unread' : ''}`;
                          const content = (
                            <>
                              <span
                                className={`navbar-dropdown__dot navbar-dropdown__dot--${item.tone}`}
                                aria-hidden="true"
                              />
                              <span className="navbar-dropdown__body">
                                <span className="navbar-dropdown__item-title">
                                  {item.title}
                                  {item.external && (
                                    <FaExternalLinkAlt
                                      className="navbar-dropdown__ext"
                                      aria-hidden="true"
                                    />
                                  )}
                                </span>
                                <span className="navbar-dropdown__item-copy">
                                  {item.body}
                                </span>
                              </span>
                              <span className="navbar-dropdown__meta">{item.time}</span>
                            </>
                          );

                          if (item.external) {
                            return (
                              <li key={item.id}>
                                <a
                                  href={item.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={className}
                                  role="menuitem"
                                  onClick={() => {
                                    markOneRead(item.id);
                                    setNotifOpen(false);
                                    closeMobileMenu();
                                  }}
                                >
                                  {content}
                                </a>
                              </li>
                            );
                          }

                          return (
                            <li key={item.id}>
                              <Link
                                to={item.href}
                                className={className}
                                role="menuitem"
                                onClick={() => {
                                  markOneRead(item.id);
                                  setNotifOpen(false);
                                  closeMobileMenu();
                                }}
                              >
                                {content}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  <button
                    type="button"
                    className={`navbar-profile${profileOpen ? ' navbar-profile--open' : ''}${location.pathname === '/conta' ? ' navbar-profile--active' : ''}`}
                    aria-label={`Conta de ${displayName}`}
                    aria-expanded={profileOpen}
                    aria-haspopup="true"
                    onClick={() => {
                      setProfileOpen((open) => !open);
                      setNotifOpen(false);
                    }}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        className="navbar-profile__avatar"
                        width={32}
                        height={32}
                      />
                    ) : (
                      <span className="navbar-profile__fallback" aria-hidden="true">
                        <FaUser />
                      </span>
                    )}
                    <span className="navbar-profile__meta">
                      <span className="navbar-profile__name">{displayName}</span>
                      <span className="navbar-profile__status">
                        {vipActive ? 'VIP ativo' : 'Online'}
                      </span>
                    </span>
                    <FaChevronDown className="navbar-profile__caret" aria-hidden="true" />
                  </button>

                  {profileOpen && (
                    <div className="navbar-dropdown navbar-dropdown--profile" role="menu">
                      <div className="navbar-dropdown__identity">
                        {avatarUrl && (
                          <img
                            src={avatarUrl}
                            alt=""
                            className="navbar-dropdown__avatar"
                            width={44}
                            height={44}
                          />
                        )}
                        <div>
                          <p className="navbar-dropdown__name">{displayName}</p>
                          <p className="navbar-dropdown__handle">
                            @{player.username || 'discord'}
                          </p>
                        </div>
                      </div>
                      <div className="navbar-dropdown__divider" />
                      <Link
                        to="/conta"
                        className="navbar-dropdown__link"
                        role="menuitem"
                        onClick={() => {
                          setProfileOpen(false);
                          closeMobileMenu();
                        }}
                      >
                        <FaUser aria-hidden="true" />
                        Minha conta
                      </Link>
                      {pages.vip && (
                        <Link
                          to="/vip"
                          className="navbar-dropdown__link"
                          role="menuitem"
                          onClick={() => {
                            setProfileOpen(false);
                            closeMobileMenu();
                          }}
                        >
                          <FaCrown aria-hidden="true" />
                          Planos VIP
                        </Link>
                      )}
                      {pages.economia && (
                        <Link
                          to="/economia"
                          className="navbar-dropdown__link"
                          role="menuitem"
                          onClick={() => {
                            setProfileOpen(false);
                            closeMobileMenu();
                          }}
                        >
                          <FaCoins aria-hidden="true" />
                          Economia
                        </Link>
                      )}
                      {pages.team && (
                        <Link
                          to="/equipe"
                          className="navbar-dropdown__link"
                          role="menuitem"
                          onClick={() => {
                            setProfileOpen(false);
                            closeMobileMenu();
                          }}
                        >
                          <FaUsers aria-hidden="true" />
                          Equipe
                        </Link>
                      )}
                      <button
                        type="button"
                        className="navbar-dropdown__link navbar-dropdown__link--danger"
                        role="menuitem"
                        onClick={logout}
                      >
                        <FaSignOutAlt aria-hidden="true" />
                        Sair
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  to="/conta"
                  className={({ isActive }) =>
                    `navbar-account${isActive ? ' navbar-account--active' : ''}`
                  }
                  onClick={closeMobileMenu}
                >
                  Entrar
                </NavLink>
              )
            )}

            <button
              type="button"
              className={`navbar-ip${copied ? ' navbar-ip--copied' : ''}`}
              onClick={copyIp}
              aria-label={copied ? 'IP copiado' : 'Copiar IP do servidor'}
            >
              {copied ? <FaCheck /> : <FaCopy />}
              <span>{copied ? 'Copiado' : 'Copiar IP'}</span>
            </button>

            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="navbar-discord"
              onClick={closeMobileMenu}
            >
              <FaDiscord className="navbar-discord-icon" aria-hidden="true" />
              <span>Discord</span>
            </a>
          </div>
        </div>

        <button
          type="button"
          className="navbar-toggle"
          onClick={() => setIsMobileMenuOpen((open) => !open)}
          aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="navbar-menu"
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
